import type { Key, Selection } from "react-aria-components";

import { useQueryClient } from "@tanstack/react-query";
import {
	Edit01,
	LayersTwo01,
	List,
	Map01,
	Map02,
	MarkerPin01,
	Modem02,
	Save01,
} from "@untitledui/icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";

import { queryKeys } from "@/api/query-keys";
import {
	ButtonGroup,
	ButtonGroupItem,
} from "@/components/ui/button-group/button-group";
import { Button } from "@/components/ui/buttons/button";
import { type ContextMenuAnchorPoint } from "@/components/ui/context-menu/pointer-context-menu";
import { Dropdown } from "@/components/ui/dropdown/dropdown-index";
import { locationPath, ROUTES } from "@/constants/routes";
import { useInstallations } from "@/features/installations/use-installations";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { cx } from "@/utils/cx";

import type { ZoneBoundaryDrawConfig } from "./map/draw/useMapboxDraw";

import { LocationManagementHeader } from "./components/location-management-header";
import { LocationManagementModals } from "./components/location-management-modals";
import {
	CreateEntityModal,
	type CreateEntityType,
	type EditEntityTarget,
} from "./components/modals";
import { useLocationManagementState } from "./hooks/useLocationManagementState";
import { useMapLocations } from "./hooks/useMapLocations";
import { adaptApiLocations } from "./utils/location/adapt-api-location";
import { formatLocationAddress } from "./utils/location/format-location-address";
import { getRefId } from "./utils/location/installation-ids";
import {
	type MapDrillBreadcrumb,
	parseSensorEditDraftCoordinates,
	sortFloorsForList,
} from "./utils/location/location-map-utils";
import {
	applyLocationEditTarget,
	parseLocationEditTarget,
} from "./utils/shared/location-edit-search-params";
import {
	applyLocationMapFocus,
	locationMapFocusKey,
	locationMapFocusMatches,
	parseLocationMapFocus,
	resolveLocationMapFocus,
	toLocationMapFocusEntities,
} from "./utils/shared/location-map-focus-search-params";
import {
	applyLocationViewMode,
	parseLocationViewMode,
	resolveLocationViewMode,
	storeLocationViewMode,
} from "./utils/shared/location-view-mode";
import { LocationListView } from "./views/list-view/LocationListView";
import { MapEmptyState } from "./views/map-view/map-pane/map-empty-state";
import {
	LocationMapViewPane,
	type MapFeatureContextMenuState,
} from "./views/map-view/map-view-pane";
import { LocationMapSidebarPane } from "./views/map-view/map-view-sidebar-pane";

const ALL_LOCATIONS_SELECT_ID = "all-locations";

/**
 * Location Management for Shadow Realm — map and list views for locations,
 * zones, and sensors. Persists via Shadow APIs
 * (docs/shadow-realm-map-migration.md).
 *
 * When `focusedLocationId` is set (from `/organization/locations/:locationId`),
 * list and map are scoped to that single location.
 */
export const LocationManagement = ({
	dimUnfocusedZones = true,
	focusedLocationId,
}: {
	dimUnfocusedZones?: boolean;
	focusedLocationId?: string;
} = {}) => {
	const navigate = useNavigate();
	const routerLocation = useLocation();
	const isSingleLocationView = Boolean(focusedLocationId);
	const [listSearchQuery, setListSearchQuery] = useState("");
	const [listViewSearchQuery, setListViewSearchQuery] = useState("");
	const [isReorderingFloors, setIsReorderingFloors] = useState(false);
	const [floorBoundaryDraw, setFloorBoundaryDraw] = useState<
		undefined | ZoneBoundaryDrawConfig
	>(undefined);
	const handleFloorBoundaryDrawChange = useCallback(
		(next: undefined | ZoneBoundaryDrawConfig) => {
			setFloorBoundaryDraw(next);
		},
		[],
	);
	const [createModal, setCreateModal] = useState<CreateEntityType | null>(null);
	const [createSensorZoneId, setCreateSensorZoneId] = useState<null | string>(
		null,
	);
	const [searchParams, setSearchParams] = useSearchParams();
	const viewMode = useMemo(
		() => resolveLocationViewMode(searchParams),
		[searchParams],
	);
	const allLocationsHref = useMemo(() => {
		// Zone/sensor focus is location-scoped — drop it when leaving the location.
		const next = applyLocationMapFocus(new URLSearchParams(searchParams), {
			floorId: null,
			sensorId: null,
			zoneId: null,
		});
		const search = next.toString();
		return search
			? `${ROUTES.ORGANIZATION_LOCATIONS}?${search}`
			: ROUTES.ORGANIZATION_LOCATIONS;
	}, [searchParams]);
	const [trackedMapViewportCenter, setTrackedMapViewportCenter] = useState<
		[number, number] | null
	>(null);
	const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
		const navState = routerLocation.state as null | {
			keepSidebarOpen?: boolean;
		};
		if (navState?.keepSidebarOpen) return true;
		if (typeof window === "undefined") return true;
		return window.matchMedia("(min-width: 1280px)").matches;
	});
	const [featureContextMenu, setFeatureContextMenu] =
		useState<MapFeatureContextMenuState | null>(null);
	const [floorEditRequest, setFloorEditRequest] = useState<null | {
		floorId: string;
		locationId: string;
		requestId: number;
	}>(null);
	// The sidebar unmounts on All Locations, which forgets that this request was
	// already applied. Drop it when the route location changes so returning
	// through a cluster opens the floor list instead of the editor.
	if (floorEditRequest && floorEditRequest.locationId !== focusedLocationId) {
		setFloorEditRequest(null);
	}
	const isDesktopLayout = useBreakpoint("xl");

	const queryClient = useQueryClient();
	const {
		dataUpdatedAt,
		isError: isLocationsError,
		isLoading: isLoadingLocations,
		locations: apiLocations,
		orgCode,
	} = useMapLocations();

	const allSourceLocations = useMemo(
		() => adaptApiLocations(apiLocations),
		[apiLocations],
	);

	const sourceLocations = useMemo(() => {
		if (!focusedLocationId) return allSourceLocations;
		return allSourceLocations.filter((entry) => entry.id === focusedLocationId);
	}, [allSourceLocations, focusedLocationId]);

	const focusedLocationMissing = Boolean(
		focusedLocationId &&
		!isLoadingLocations &&
		!allSourceLocations.some((entry) => entry.id === focusedLocationId),
	);

	const invalidateLocations = useCallback(() => {
		if (!orgCode) return;
		void queryClient.invalidateQueries({
			queryKey: queryKeys.locations.all(orgCode),
		});
	}, [orgCode, queryClient]);

	const { data: installations = [], isSuccess: installationsReady } =
		useInstallations();

	const locationInstallationCounts = useMemo(() => {
		const counts = new Map<string, number>();
		for (const installation of installations) {
			const locationId = getRefId(installation.location);
			if (!locationId) continue;
			counts.set(locationId, (counts.get(locationId) ?? 0) + 1);
		}
		return counts;
	}, [installations]);

	const locationIdsWithInstallation = useMemo(
		() => new Set(locationInstallationCounts.keys()),
		[locationInstallationCounts],
	);

	/**
	 * Open the sidebar only when the location already has an installation,
	 * zone, and sensor. Resolved synchronously on select so map fit runs with
	 * the correct camera padding.
	 */
	const shouldOpenSidebarForLocationId = useCallback(
		(locationId: string): boolean => {
			const loc = sourceLocations.find((entry) => entry.id === locationId);
			if (!loc) return false;

			if (!locationIdsWithInstallation.has(locationId)) return false;

			const locZones = loc.zones ?? [];
			if (locZones.length === 0) return false;
			return locZones.some((zone) => (zone.sensorIds?.length ?? 0) > 0);
		},
		[locationIdsWithInstallation, sourceLocations],
	);

	const handleLocationSelected = useCallback(
		(locationId: string) => {
			setIsSidebarOpen(shouldOpenSidebarForLocationId(locationId));
		},
		[shouldOpenSidebarForLocationId],
	);

	const lastAppliedMapFocusKeyRef = useRef<null | string>(null);
	/**
	 * React Router's `setSearchParams` updaters do not chain like `useState` —
	 * a late popup-close write still sees the pre-list URL (`view=map`) and can
	 * overwrite `view=list`. Set this synchronously before navigating to list.
	 */
	const suppressMapFocusUrlSyncRef = useRef(false);

	const navigateToLocation = useCallback(
		(locationId: string) => {
			if (locationId === focusedLocationId) return;
			// Zone/sensor query params belong to the previous location.
			const next = applyLocationMapFocus(new URLSearchParams(searchParams), {
				floorId: null,
				sensorId: null,
				zoneId: null,
			});
			const search = next.toString();
			lastAppliedMapFocusKeyRef.current = null;
			void navigate({
				pathname: locationPath(locationId),
				search: search ? `?${search}` : "",
			});
		},
		[focusedLocationId, navigate, searchParams],
	);

	/** Keep `floor` / `zone` / `sensor` query params in sync with the map. */
	const handleMapFocusChange = useCallback(
		(
			focus: {
				floorId?: null | string;
				sensorId?: null | string;
				zoneId?: null | string;
			},
			options?: { recordApplied?: boolean },
		) => {
			const floorId = focus.floorId ?? null;
			const zoneId = focus.zoneId ?? null;
			const sensorId = focus.sensorId ?? null;
			const recordApplied = options?.recordApplied !== false;
			if (suppressMapFocusUrlSyncRef.current) return;
			if (recordApplied && viewMode !== "map") return;
			setSearchParams(
				(current) => {
					if (recordApplied && resolveLocationViewMode(current) !== "map") {
						return current;
					}
					if (recordApplied && focusedLocationId) {
						lastAppliedMapFocusKeyRef.current = locationMapFocusKey(
							focusedLocationId,
							{ floorId, sensorId, zoneId },
						);
					}
					return applyLocationMapFocus(current, {
						floorId,
						sensorId,
						zoneId,
					});
				},
				{ replace: true },
			);
		},
		[focusedLocationId, setSearchParams, viewMode],
	);

	const urlMapFocus = useMemo(
		() => parseLocationMapFocus(searchParams),
		[searchParams],
	);

	const mapState = useLocationManagementState({
		dimUnfocusedZones,
		focusedLocationId,
		locationIdsWithInstallation,
		onLocationClusterSelect: navigateToLocation,
		onLocationSelected: handleLocationSelected,
		onLocationsMutated: invalidateLocations,
		onMapFocusChange: handleMapFocusChange,
		sourceLocations: orgCode ? sourceLocations : null,
		sourceRevision: orgCode
			? `${orgCode}:${dataUpdatedAt}:${focusedLocationId ?? "all"}`
			: null,
		urlMapFocus,
	});
	const {
		cancelSensorEdit,
		cancelZoneEdit,
		canEditSensorDetails,
		canEditZoneDetails,
		clearLocationSelection,
		editDraft,
		editingSensorId,
		editingZoneId,
		handleMapBreadcrumbClick,
		handleMarkerEdit,
		handleOverlayDoubleClick,
		isFloorEditActive,
		isLocationSelected,
		location,
		locations,
		mappableLocationCount,
		revealOnMap,
		selectedLocationId,
		selectFloor,
		selectLocation,
		totalSensors,
		unmappedLocationCount,
		visibleMapCenter,
		zones,
	} = mapState;

	/** Open map view focused on a location, optional zone, optional sensor. */
	const handleViewOnMap = useCallback(
		(focus: { locationId: string; sensorId?: string; zoneId?: string }) => {
			const resolved = resolveLocationMapFocus(
				{
					sensorId: focus.sensorId ?? null,
					zoneId: focus.zoneId ?? null,
				},
				toLocationMapFocusEntities(
					locations.filter((entry) => entry.id === focus.locationId),
				),
			);
			const next = applyLocationEditTarget(
				applyLocationMapFocus(
					applyLocationViewMode(new URLSearchParams(searchParams), "map"),
					resolved,
				),
				null,
			);
			const search = next.toString();
			setIsSidebarOpen(true);
			void navigate({
				pathname: locationPath(focus.locationId),
				search: search ? `?${search}` : "",
			});
		},
		[locations, navigate, searchParams],
	);

	// Hydrate floor/zone/sensor selection from the URL when on map view, and
	// correct params whose relationship no longer matches the location.
	// Do not depend on `mapMarkers` / `revealOnMap`: opening a zone rebuilds
	// markers and would re-run this effect against a stale URL mid-toggle,
	// which skips first-click scroll and re-opens on deselect. Pending sensor
	// selection is handled inside map state via `pendingActiveSensorIdRef`.
	useEffect(() => {
		const scope = focusedLocationId
			? locations.filter((entry) => entry.id === focusedLocationId)
			: locations;
		if (focusedLocationId && scope.length === 0) return;
		if (!focusedLocationId && locations.length === 0) return;

		const raw = parseLocationMapFocus(searchParams);
		const resolved = resolveLocationMapFocus(
			raw,
			toLocationMapFocusEntities(scope),
		);
		const rawIsEmpty = !raw.floorId && !raw.zoneId && !raw.sensorId;
		const differs = !locationMapFocusMatches(raw, resolved);

		if (viewMode === "map" && focusedLocationId) {
			if (selectedLocationId !== focusedLocationId) return;
			if (location.id !== focusedLocationId) return;

			const key = locationMapFocusKey(focusedLocationId, resolved);
			if (lastAppliedMapFocusKeyRef.current === key) return;

			if (rawIsEmpty) {
				const firstFloorId =
					sortFloorsForList(location.floors ?? [])[0]?.id ?? null;
				if (firstFloorId) {
					const floorFocus = {
						floorId: firstFloorId,
						sensorId: null,
						zoneId: null,
					};
					const floorKey = locationMapFocusKey(focusedLocationId, floorFocus);
					if (lastAppliedMapFocusKeyRef.current === floorKey) return;
					const applied = revealOnMap(floorFocus);
					if (!applied) return;
					lastAppliedMapFocusKeyRef.current = floorKey;
					return;
				}

				const previous = lastAppliedMapFocusKeyRef.current;
				if (previous && previous !== key) {
					const applied = revealOnMap(resolved);
					if (!applied) return;
				}
				lastAppliedMapFocusKeyRef.current = key;
				return;
			}

			const applied = revealOnMap(resolved);
			if (!applied) return;

			lastAppliedMapFocusKeyRef.current = key;
			if (differs) handleMapFocusChange(resolved);
			return;
		}

		if (!differs || rawIsEmpty) return;
		handleMapFocusChange(resolved, { recordApplied: false });
		// eslint-disable-next-line react-hooks/exhaustive-deps -- see comment above
	}, [
		focusedLocationId,
		location,
		locations,
		searchParams,
		selectedLocationId,
		viewMode,
	]);

	const editTarget = useMemo(
		() => parseLocationEditTarget(searchParams, locations),
		[locations, searchParams],
	);

	const setEditTarget = useCallback(
		(target: EditEntityTarget | null) => {
			setSearchParams((current) => applyLocationEditTarget(current, target), {
				replace: true,
			});
		},
		[setSearchParams],
	);

	// Keep `view` in the URL when missing/invalid so refresh and shares match
	// the stored preference (URL wins once present). Mirror a valid URL value
	// into localStorage so the next visit without `view` keeps the preference.
	useEffect(() => {
		const fromUrl = parseLocationViewMode(searchParams);
		if (fromUrl) {
			storeLocationViewMode(fromUrl);
			return;
		}
		setSearchParams(
			(current) =>
				applyLocationViewMode(current, resolveLocationViewMode(current)),
			{ replace: true },
		);
	}, [searchParams, setSearchParams]);

	useEffect(() => {
		if (!isSidebarOpen) return;

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				setIsSidebarOpen(false);
			}
		};

		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [isSidebarOpen]);

	// Open the drawer when edit mode starts (or the edit target changes) so the
	// form is reachable; the operator can still close it to work on the map.
	useEffect(() => {
		if (!editingSensorId && !editingZoneId) return;
		// Defer so we don't setState synchronously in the effect body.
		const frame = requestAnimationFrame(() => {
			setIsSidebarOpen(true);
		});
		return () => cancelAnimationFrame(frame);
	}, [editingSensorId, editingZoneId]);

	const openSidebar = useCallback(() => setIsSidebarOpen(true), []);
	const closeSidebar = useCallback(() => setIsSidebarOpen(false), []);

	const locationInstallationCount = useMemo(() => {
		if (!isLocationSelected) return 0;
		return installations.filter(
			(installation) => getRefId(installation.location) === location.id,
		).length;
	}, [installations, isLocationSelected, location.id]);

	const locationHasZonesAndSensors = useMemo(() => {
		if (zones.length === 0) return false;
		return zones.some((zone) => (zone.sensorIds?.length ?? 0) > 0);
	}, [zones]);

	/**
	 * If installations load after location select, re-apply the open rule once.
	 * Including sidebar state in fitBoundsKey reframes the map with padding.
	 */
	const sidebarSyncedInstallReadyRef = useRef(false);
	const sidebarSyncedLocationIdRef = useRef<null | string>(null);

	useEffect(() => {
		if (!isLocationSelected) {
			sidebarSyncedLocationIdRef.current = null;
			sidebarSyncedInstallReadyRef.current = false;
			return;
		}

		if (sidebarSyncedLocationIdRef.current !== location.id) {
			sidebarSyncedLocationIdRef.current = location.id;
			sidebarSyncedInstallReadyRef.current = false;
		}

		if (!installationsReady || sidebarSyncedInstallReadyRef.current) return;
		sidebarSyncedInstallReadyRef.current = true;

		const shouldOpen =
			locationInstallationCount > 0 && locationHasZonesAndSensors;
		const frame = requestAnimationFrame(() => {
			setIsSidebarOpen(shouldOpen);
		});
		return () => cancelAnimationFrame(frame);
	}, [
		installationsReady,
		isLocationSelected,
		location.id,
		locationHasZonesAndSensors,
		locationInstallationCount,
	]);

	const handleBreadcrumbClick = useCallback(
		(crumb: MapDrillBreadcrumb) => {
			if (
				isSingleLocationView &&
				(crumb.id === "all-locations" || crumb.level === "locations")
			) {
				void navigate(allLocationsHref);
				return;
			}
			handleMapBreadcrumbClick(crumb);
			if (crumb.id === "all-locations" || crumb.level === "locations") {
				setIsSidebarOpen(false);
			}
		},
		[
			allLocationsHref,
			handleMapBreadcrumbClick,
			isSingleLocationView,
			navigate,
		],
	);

	const sidebarToggleLabel = isLocationSelected
		? "Open zones and sensors"
		: "Open locations";
	const isOverlayDrawer = isSidebarOpen && !isDesktopLayout;

	/** 28rem overlay panel — keeps focus in the unobscured map area. */
	const mapCameraPadding = useMemo((): {
		bottom: number;
		left: number;
		right: number;
		top: number;
	} => {
		if (!isSidebarOpen) {
			return { bottom: 0, left: 0, right: 0, top: 0 };
		}
		return { bottom: 0, left: 0, right: 448, top: 0 };
	}, [isSidebarOpen]);

	const editingSensorHasCoordinates = Boolean(
		editDraft && parseSensorEditDraftCoordinates(editDraft),
	);
	const canPlaceSensorByMapClick = Boolean(
		isLocationSelected &&
		canEditSensorDetails &&
		editingSensorId &&
		!editingSensorHasCoordinates,
	);

	const mapCenter = useMemo(
		(): [number, number] => visibleMapCenter ?? [-105.0155, 39.74],
		[visibleMapCenter],
	);
	const mapViewportCenter = trackedMapViewportCenter ?? mapCenter;

	const locationSelectItems = useMemo(
		() => [
			{ id: ALL_LOCATIONS_SELECT_ID, label: "All Locations" },
			...allSourceLocations.map((entry) => ({
				id: entry.id,
				label: entry.name,
			})),
		],
		[allSourceLocations],
	);

	const openFeatureContextMenu = useCallback(
		(
			kind: MapFeatureContextMenuState["kind"],
			feature: GeoJSON.Feature,
			point: ContextMenuAnchorPoint,
		) => {
			if (!isLocationSelected) return;
			if (kind === "sensor" && !canEditSensorDetails) return;
			if (kind === "zone" && !canEditZoneDetails) return;
			setFeatureContextMenu({ anchor: point, feature, kind });
		},
		[canEditSensorDetails, canEditZoneDetails, isLocationSelected],
	);

	const handleMarkerContextMenu = useCallback(
		(feature: GeoJSON.Feature, point: ContextMenuAnchorPoint) => {
			if (isFloorEditActive) return;
			openFeatureContextMenu("sensor", feature, point);
		},
		[isFloorEditActive, openFeatureContextMenu],
	);

	const handleOverlayContextMenu = useCallback(
		(feature: GeoJSON.Feature, point: ContextMenuAnchorPoint) => {
			if (isFloorEditActive) return;
			if (feature.properties?.overlayKind === "floor") {
				openFeatureContextMenu("floor", feature, point);
				return;
			}
			openFeatureContextMenu("zone", feature, point);
		},
		[isFloorEditActive, openFeatureContextMenu],
	);

	const handleFeatureContextMenuAction = useCallback(
		(key: Key) => {
			if (!featureContextMenu || key !== "edit") return;

			if (featureContextMenu.kind === "sensor") {
				handleMarkerEdit(featureContextMenu.feature);
				return;
			}

			if (featureContextMenu.kind === "floor") {
				const floorId = featureContextMenu.feature.properties?.floorId;
				if (typeof floorId !== "string") return;
				if (editingSensorId) cancelSensorEdit();
				if (editingZoneId) cancelZoneEdit();
				if (!focusedLocationId) return;
				selectFloor(floorId);
				setIsReorderingFloors(false);
				setFloorEditRequest((current) => ({
					floorId,
					locationId: focusedLocationId,
					requestId: (current?.requestId ?? 0) + 1,
				}));
				return;
			}

			handleOverlayDoubleClick(featureContextMenu.feature);
		},
		[
			cancelSensorEdit,
			cancelZoneEdit,
			editingSensorId,
			editingZoneId,
			featureContextMenu,
			focusedLocationId,
			handleMarkerEdit,
			handleOverlayDoubleClick,
			selectFloor,
		],
	);

	const contextMenuItems = useMemo(
		() => [
			{
				id: "edit",
				label:
					featureContextMenu?.kind === "floor"
						? "Edit floor"
						: featureContextMenu?.kind === "zone"
							? "Edit zone"
							: "Edit sensor",
			},
		],
		[featureContextMenu?.kind],
	);

	const sectionClassName = cx(
		"flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden rounded-xl bg-primary shadow-xs ring-1 ring-secondary",
	);

	const countLabel = (count: number, singular: string, plural: string) =>
		`${count} ${count === 1 ? singular : plural}`;

	const isLocationHeader =
		isSingleLocationView || (viewMode === "map" && isLocationSelected);

	const headerBadges = isLocationHeader
		? [
				countLabel(zones.length, "Zone", "Zones"),
				countLabel(totalSensors, "Sensor", "Sensors"),
			]
		: [countLabel(locations.length, "Location", "Locations")];

	const headerDescription = isLocationHeader
		? formatLocationAddress(location)
		: viewMode === "list"
			? "Expand a location for zones, then a zone for sensors. Use View location to open a single-location page, or switch to map view for spatial editing."
			: unmappedLocationCount > 0
				? "Select a location on the map or from the sidebar. Locations without a map position only appear in the sidebar."
				: "Click a location on the map to open its zones and sensors in the sidebar.";

	const handleBackToAllLocations = useCallback(() => {
		setEditTarget(null);
		if (isSingleLocationView) {
			// Route change remounts this page — carry open-sidebar intent so the
			// all-locations list stays visible instead of snapping shut.
			void navigate(allLocationsHref, {
				state: isSidebarOpen ? { keepSidebarOpen: true } : undefined,
			});
			return;
		}
		clearLocationSelection();
	}, [
		allLocationsHref,
		clearLocationSelection,
		isSidebarOpen,
		isSingleLocationView,
		navigate,
		setEditTarget,
	]);

	const handleSelectLocationFromList = useCallback(
		(locationId: string) => {
			setListSearchQuery("");
			navigateToLocation(locationId);
		},
		[navigateToLocation],
	);

	const handleViewLocationFromList = navigateToLocation;

	const handleSelectLocationFromSwitcher = useCallback(
		(locationId: string) => {
			setListSearchQuery("");
			navigateToLocation(locationId);
		},
		[navigateToLocation],
	);

	const handleEditLocationFromList = useCallback(
		(locationId: string) => {
			setEditTarget({ locationId, type: "location" });
		},
		[setEditTarget],
	);

	const handleEditFloorFromList = useCallback(
		(locationId: string, floorId: string) => {
			setEditTarget({ floorId, locationId, type: "floor" });
		},
		[setEditTarget],
	);

	const handleEditZoneFromList = useCallback(
		(locationId: string, zoneId: string) => {
			setEditTarget({ locationId, type: "zone", zoneId });
		},
		[setEditTarget],
	);

	const handleEditSensorFromList = useCallback(
		(locationId: string, sensorId: string) => {
			setEditTarget({ locationId, sensorId, type: "sensor" });
		},
		[setEditTarget],
	);

	const handleSelectSensorFromEditModal = useCallback(
		(sensorId: string) => {
			const locationId =
				locations.find((entry) =>
					(entry.zones ?? []).some((zone) =>
						(zone.sensors ?? []).some((sensor) => sensor.id === sensorId),
					),
				)?.id ?? null;
			if (!locationId) return;
			setEditTarget({ locationId, sensorId, type: "sensor" });
		},
		[locations, setEditTarget],
	);

	const handleAddInstallationFromList = useCallback(
		(locationId: string) => {
			setEditTarget(null);
			selectLocation(locationId);
			setCreateModal("installation");
		},
		[selectLocation, setEditTarget],
	);

	const handleAddFloorFromList = useCallback(
		(locationId: string) => {
			setEditTarget(null);
			selectLocation(locationId);
			setCreateModal("floor");
		},
		[selectLocation, setEditTarget],
	);

	const handleAddZoneFromList = useCallback(
		(locationId: string) => {
			setEditTarget(null);
			selectLocation(locationId);
			setCreateModal("zone");
		},
		[selectLocation, setEditTarget],
	);

	const handleAddSensorFromList = useCallback(
		(locationId: string, zoneId: string) => {
			setEditTarget(null);
			selectLocation(locationId);
			setCreateSensorZoneId(zoneId);
			setCreateModal("sensor");
		},
		[selectLocation, setEditTarget],
	);

	const handleViewModeChange = useCallback(
		(keys: Selection) => {
			const selected = [...keys][0];
			if (selected !== "list" && selected !== "map") return;
			// Block map-focus URL writes before navigate; popup close on map
			// unmount otherwise races with a stale `view=map` snapshot.
			suppressMapFocusUrlSyncRef.current = selected === "list";
			storeLocationViewMode(selected);
			// Keep `zone` / `sensor` across map↔list so both views share focus.
			setSearchParams((current) => applyLocationViewMode(current, selected), {
				replace: true,
			});
		},
		[setSearchParams],
	);

	const listMapFocus = useMemo(
		() => parseLocationMapFocus(searchParams),
		[searchParams],
	);

	const showAllUnmappedEmpty =
		!isLocationSelected && locations.length > 0 && mappableLocationCount === 0;

	const showUnmappedBanner =
		!isLocationSelected &&
		mappableLocationCount > 0 &&
		unmappedLocationCount > 0;

	const installationDisabledReason = !isLocationSelected
		? "Select a location first"
		: undefined;
	const editLocationDisabledReason = !isLocationSelected
		? "Select a location first"
		: undefined;
	const floorDisabledReason = !isLocationSelected
		? "Select a location first"
		: locationInstallationCount === 0
			? "Create an installation for this location first"
			: undefined;
	const zoneDisabledReason = !isLocationSelected
		? "Select a location first"
		: locationInstallationCount === 0
			? "Create an installation for this location first"
			: undefined;
	const sensorDisabledReason = !isLocationSelected
		? "Select a location first"
		: zones.length === 0
			? "Create a zone first"
			: undefined;

	const handleCreateMenuAction = useCallback(
		(key: Key) => {
			const action = String(key);
			if (action === "edit-location") {
				if (!isLocationSelected) return;
				setEditTarget({ locationId: location.id, type: "location" });
				return;
			}
			if (action === "location") {
				setCreateModal("location");
				return;
			}
			if (action === "installation") {
				setCreateModal("installation");
				return;
			}
			if (action === "floor") {
				setCreateModal("floor");
				return;
			}
			if (action === "zone") {
				setCreateModal("zone");
				return;
			}
			if (action === "sensor") {
				setCreateSensorZoneId(null);
				setCreateModal("sensor");
			}
		},
		[isLocationSelected, location.id, setEditTarget],
	);

	const viewModeToggle = (
		<ButtonGroup
			aria-label="Location view mode"
			disallowEmptySelection
			onSelectionChange={handleViewModeChange}
			selectedKeys={new Set([viewMode])}
			size="sm"
		>
			<ButtonGroupItem aria-label="Map view" iconLeading={Map01} id="map" />
			<ButtonGroupItem aria-label="List view" iconLeading={List} id="list" />
		</ButtonGroup>
	);

	const createMenu = (
		<Dropdown.Root>
			<Dropdown.DotsButton
				aria-label="Location management actions"
				className="flex size-9 items-center justify-center rounded-lg text-fg-quaternary ring-1 ring-brand ring-inset hover:bg-primary_hover"
			/>
			<Dropdown.Popover className="min-w-56" placement="bottom end">
				<Dropdown.Menu
					aria-label="Location management actions"
					onAction={handleCreateMenuAction}
					selectionMode="none"
				>
					{isSingleLocationView ? null : (
						<>
							<Dropdown.Item
								icon={MarkerPin01}
								id="location"
								label="Add location"
							/>
							<Dropdown.Separator />
						</>
					)}
					<Dropdown.Item
						description={editLocationDisabledReason}
						icon={Edit01}
						id="edit-location"
						isDisabled={Boolean(editLocationDisabledReason)}
						label="Edit location"
					/>
					<Dropdown.Item
						description={installationDisabledReason}
						icon={Save01}
						id="installation"
						isDisabled={Boolean(installationDisabledReason)}
						label="Add installation"
					/>
					<Dropdown.Item
						description={floorDisabledReason}
						icon={LayersTwo01}
						id="floor"
						isDisabled={Boolean(floorDisabledReason)}
						label="Add floor"
					/>
					<Dropdown.Item
						description={zoneDisabledReason}
						icon={Map02}
						id="zone"
						isDisabled={Boolean(zoneDisabledReason)}
						label="Add zone"
					/>
					<Dropdown.Item
						description={sensorDisabledReason}
						icon={Modem02}
						id="sensor"
						isDisabled={Boolean(sensorDisabledReason)}
						label="Add sensor"
					/>
				</Dropdown.Menu>
			</Dropdown.Popover>
		</Dropdown.Root>
	);

	const headerActions = (
		<div className="flex flex-wrap items-center justify-end gap-2">
			{viewModeToggle}
			{createMenu}
		</div>
	);

	if (!orgCode) {
		return (
			<section className={sectionClassName}>
				<MapEmptyState
					description="Choose an organization in the sidebar to load its locations."
					fullHeight
					icon={<MarkerPin01 />}
					title="Select an Organization"
				/>
			</section>
		);
	}

	if (isLoadingLocations && locations.length === 0) {
		return (
			<section className={sectionClassName}>
				<MapEmptyState
					description="Fetching locations for the selected organization…"
					fullHeight
					icon={<Map02 />}
					loading
					title="Loading Locations"
				/>
			</section>
		);
	}

	if (isLocationsError && locations.length === 0) {
		return (
			<section className={sectionClassName}>
				<MapEmptyState
					description="Could not load locations for this organization. Try again or pick another org."
					fullHeight
					icon={<Map02 />}
					title="Failed to Load Locations"
				/>
			</section>
		);
	}

	if (focusedLocationMissing) {
		return (
			<section className={sectionClassName}>
				<MapEmptyState
					action={
						<Button
							color="secondary"
							onClick={() => {
								void navigate(allLocationsHref);
							}}
							size="sm"
						>
							Back to all locations
						</Button>
					}
					description="That location was not found for this organization. It may have been deleted or the link is incorrect."
					fullHeight
					icon={<MarkerPin01 />}
					title="Location Not Found"
				/>
			</section>
		);
	}

	if (!isLoadingLocations && locations.length === 0) {
		return (
			<section className={sectionClassName}>
				<MapEmptyState
					action={
						<Button
							color="primary"
							onClick={() => setCreateModal("location")}
							size="sm"
						>
							Add location
						</Button>
					}
					description="This organization has no locations yet. Create one to start mapping zones and sensors."
					fullHeight
					icon={<MarkerPin01 />}
					title="No Locations"
				/>
				<CreateEntityModal
					onClose={() => setCreateModal(null)}
					onLocationCreated={(locationId) => {
						selectLocation(locationId);
					}}
					type={createModal}
				/>
			</section>
		);
	}

	return (
		<section className={sectionClassName}>
			<LocationManagementHeader
				actions={headerActions}
				backHref={isSingleLocationView ? allLocationsHref : undefined}
				badges={headerBadges}
				description={headerDescription}
				onEdit={
					isSingleLocationView || isLocationSelected
						? () => setEditTarget({ locationId: location.id, type: "location" })
						: undefined
				}
				title={
					isSingleLocationView
						? location.name
						: viewMode === "list"
							? "Locations"
							: isLocationSelected
								? location.name
								: "Locations"
				}
			/>

			<div className="relative flex min-h-0 flex-1 flex-col overflow-hidden p-4 md:p-6">
				{viewMode === "list" ? (
					<LocationListView
						focusFloorId={listMapFocus.floorId}
						focusSensorId={listMapFocus.sensorId}
						focusZoneId={listMapFocus.zoneId}
						locationInstallationCounts={locationInstallationCounts}
						locations={locations}
						onAddFloor={handleAddFloorFromList}
						onAddInstallation={handleAddInstallationFromList}
						onAddSensor={handleAddSensorFromList}
						onAddZone={handleAddZoneFromList}
						onEditFloor={handleEditFloorFromList}
						onEditLocation={handleEditLocationFromList}
						onEditSensor={handleEditSensorFromList}
						onEditZone={handleEditZoneFromList}
						onSearchQueryChange={setListViewSearchQuery}
						onSelectLocation={
							isSingleLocationView ? undefined : handleSelectLocationFromList
						}
						onViewLocation={
							isSingleLocationView ? undefined : handleViewLocationFromList
						}
						onViewOnMap={handleViewOnMap}
						searchQuery={listViewSearchQuery}
						selectedLocationId={
							isLocationSelected ? location.id : selectedLocationId
						}
						variant={isSingleLocationView ? "zones" : "locations"}
					/>
				) : (
					<div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
						<LocationMapViewPane
							canPlaceSensorByMapClick={canPlaceSensorByMapClick}
							contextMenuItems={contextMenuItems}
							editingSensorHasCoordinates={editingSensorHasCoordinates}
							featureContextMenu={featureContextMenu}
							floorBoundaryDraw={floorBoundaryDraw}
							handleBackToAllLocations={handleBackToAllLocations}
							handleBreadcrumbClick={handleBreadcrumbClick}
							handleFeatureContextMenuAction={handleFeatureContextMenuAction}
							handleMarkerContextMenu={handleMarkerContextMenu}
							handleOverlayContextMenu={handleOverlayContextMenu}
							isOverlayDrawer={isOverlayDrawer}
							isSidebarOpen={isSidebarOpen}
							locationInstallationCount={locationInstallationCount}
							mapCameraPadding={mapCameraPadding}
							mapCenter={mapCenter}
							mapState={mapState}
							onCloseSidebar={closeSidebar}
							onOpenCreateInstallation={() => setCreateModal("installation")}
							onOpenCreateSensor={(zoneId) => {
								setCreateSensorZoneId(zoneId ?? null);
								setCreateModal("sensor");
							}}
							onOpenCreateZone={() => setCreateModal("zone")}
							onOpenSidebar={openSidebar}
							setFeatureContextMenu={setFeatureContextMenu}
							setTrackedMapViewportCenter={setTrackedMapViewportCenter}
							showAllUnmappedEmpty={showAllUnmappedEmpty}
							showUnmappedBanner={showUnmappedBanner}
							sidebarToggleLabel={sidebarToggleLabel}
						/>

						<LocationMapSidebarPane
							floorEditRequest={floorEditRequest}
							handleBackToAllLocations={handleBackToAllLocations}
							handleSelectLocationFromList={handleSelectLocationFromList}
							handleSelectLocationFromSwitcher={
								handleSelectLocationFromSwitcher
							}
							isReorderingFloors={isReorderingFloors}
							isSidebarOpen={isSidebarOpen}
							listSearchQuery={listSearchQuery}
							locationIdsWithInstallation={locationIdsWithInstallation}
							locationInstallationCount={locationInstallationCount}
							locationSelectItems={locationSelectItems}
							mapState={mapState}
							mapViewportCenter={mapViewportCenter}
							onAddFloor={() => setCreateModal("floor")}
							onAddInstallation={() => setCreateModal("installation")}
							onAddSensor={(zoneId) => {
								setCreateSensorZoneId(zoneId);
								setCreateModal("sensor");
							}}
							onAddZone={() => setCreateModal("zone")}
							onCloseSidebar={closeSidebar}
							onEditLocation={() =>
								setEditTarget({ locationId: location.id, type: "location" })
							}
							onFloorBoundaryDrawChange={handleFloorBoundaryDrawChange}
							setIsReorderingFloors={setIsReorderingFloors}
							setListSearchQuery={setListSearchQuery}
						/>
					</div>
				)}
			</div>

			<LocationManagementModals
				createModal={createModal}
				createSensorZoneId={createSensorZoneId}
				editTarget={editTarget}
				isLocationSelected={isLocationSelected}
				location={location}
				locations={locations}
				mapCenter={mapCenter}
				onCloseCreate={() => {
					setCreateModal(null);
					setCreateSensorZoneId(null);
				}}
				onCloseEdit={() => setEditTarget(null)}
				onLocationCreated={(locationId) => {
					selectLocation(locationId);
				}}
				onSelectSensor={handleSelectSensorFromEditModal}
			/>
		</section>
	);
};
